import {
  List,
  ListProps,
  SelectionState,
} from "@monolith-forensics/monolith-ui";
import { UsersRound } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { GetContactsQuery } from "../contacts.api";
import { isAssignedToCase } from "../contacts.helpers";
import { ContactsQuery } from "../contacts.query";
import { Contact } from "../contacts.types";
import { ContactItem } from "./ContactsList.item";
import { ContactsListHandle, useContactSelection } from "./useContactSelection";

export type { ContactsListHandle };

/**
 * `height` and the bounds beside it are List's own props, forwarded untouched.
 * Worth knowing before reaching for them: List's root is `height: 0; flex: 1 1
 * auto`, so this component only has a size inside a flex column that already
 * has one, and `height` lands on the scroll area as a flex *basis* rather than
 * a cap — a bounded parent grows or shrinks it past whatever is passed here.
 * Sizing therefore belongs to the caller's layout, and the common case passes
 * nothing at all.
 */
export interface ContactsListProps
  extends Pick<
    ListProps<Contact>,
    "height" | "maxHeight" | "minHeight" | "onItemClick" | "onItemDoubleClick"
  > {
  /**
   * Scopes the underlying request.
   *
   * `{ case_id }` narrows the list *to* one case's contacts (the API INNER
   * JOINs contact_assign). For a picker that assigns contacts to a case that is
   * backwards — pass `{ assigned_case_id }` instead, which keeps the set whole
   * and flags the ones already on the case so they cannot be picked again.
   */
  query?: GetContactsQuery;
  /**
   * Fires with the whole selected Contact objects and selectionState whenever the selection — or
   * the set of loaded contacts backing it — changes.
   */
  onSelectionChange?: (e: {
    contacts: Contact[];
    selectionState: SelectionState;
  }) => void;
}

export const ContactsList = forwardRef<ContactsListHandle, ContactsListProps>(
  ({ query, onSelectionChange, ...listProps }, ref) => {
    const [search, setSearch] = useState("");

    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
      ContactsQuery.useGetInfiniteContacts({ ...query, search });

    const contacts = useMemo(
      () => data?.pages.flatMap((page) => page.data) ?? [],
      [data]
    );
    const total = data?.pages[0]?.total;

    const {
      selectionState,
      selectedContacts,
      handleSelectionChange,
      pinSelection,
      handle,
    } = useContactSelection({ contacts, total });

    useImperativeHandle(ref, () => handle, [handle]);

    // Held in a ref so an inline callback's changing identity cannot re-fire the
    // effect on every parent render.
    const onSelectionChangeRef = useRef(onSelectionChange);
    onSelectionChangeRef.current = onSelectionChange;

    useEffect(() => {
      onSelectionChangeRef.current?.({
        contacts: selectedContacts,
        selectionState,
      });
    }, [selectedContacts, selectionState]);

    /*
      Pins the selection before the query it was made against changes.

      Guarded on an actual change because the search is debounced and a blanket
      selection is worth keeping while it stands: a repeat of the same term
      would stand it down for nothing, and with it the one thing that carries
      the pages this list has not loaded yet.
    */
    const handleSearch = (nextSearch: string) => {
      if (nextSearch === search) return;

      setSearch(nextSearch);
      pinSelection();
    };

    return (
      <List<Contact>
        data={contacts}
        keyField="client_id"
        totalRecords={total}
        itemHeight={60}
        loading={isLoading}
        loadingMore={isFetchingNextPage}
        enableSelection
        /*
          Blocks the row at the source: the checkbox renders disabled, clicks
          and Space no-op, shift-ranges skip it, and a key already in the
          selection is released if a refetch flags it. Everything a picker needs
          for "this one is already on the case" — leaving the hook's
          selectedContacts as the guarantee for what the caller receives.
        */
        isItemSelectable={(contact) => !isAssignedToCase(contact)}
        enableKeyboardNavigation
        selectionState={selectionState}
        onSelectionChange={handleSelectionChange}
        selectionVisibility="always"
        selectOnRowClick={true}
        showGridLines={true}
        disableSelectAll={true}
        // Paging and search are server-side. Sorting is not offered at all: local
        // sorting would only order the loaded pages, and GET /contacts takes no
        // order param to do it properly.
        manualSearch
        searchState={search}
        onSearch={handleSearch}
        hasNextPage={hasNextPage}
        onLoadMore={() => void fetchNextPage()}
        renderItem={({ item }) => <ContactItem contact={item} />}
        enableSelectionModifiers
        listMenuOptions={{
          enabled: true,
          searchOptions: {
            enabled: true,
            placeholder: "Search contacts...",
            fullWidth: true,
          },
        }}
        emptyStateOptions={{
          title: "No Contacts Found",
          description: search
            ? "No contacts match your search."
            : "No contacts have been created yet.",
          icon: UsersRound,
        }}
        {...listProps}
      />
    );
  }
);

ContactsList.displayName = "ContactsList";
